import { ProductAssociation } from 'components/Collection/pages/product/AssociatedProducts/types';
import { BottomLine, ProductReview, ProductReviewPagination, Item, JsonResponse, Bundle, Product } from '../types';
import { getLocalURL } from '../utils/utils';

export interface ProductReviewsQueryResponse {
  productReviews: {
    reviews: ProductReview[];
    bottomLine: BottomLine;
    pagination: ProductReviewPagination;
  };
}

export interface SiteReviewsQueryResponse {
  siteReviews: {
    reviews: ProductReview[];
    bottomLine: BottomLine;
  };
}

const productsApi = {
  async getProductReviews(page: number, bundleId: number): Promise<ProductReviewsQueryResponse> {
    const response = await fetch(`${getLocalURL()}/api/get-product-reviews`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: bundleId,
        perPage: 3,
        page: page,
      }),
    });
    const reviews = await response.json();
    return reviews;
  },
};

export const getSiteReviews = async (page: number = 1, perPage: number = 20): Promise<SiteReviewsQueryResponse> => {
  const response = await fetch(`${getLocalURL()}/api/get-all-site-reviews`, {
    method: 'POST',
    body: JSON.stringify({
      perPage: perPage,
      page: page,
    }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (response.status !== 200) {
    throw new Error('Failed to site reviews from the API');
  }

  return await response.json();
};

export const getProductAssociations = async (productId: number): Promise<JsonResponse<ProductAssociation[]>> =>
  await fetch(`${getLocalURL()}/api/get-product-associations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      productId: productId,
    }),
  });

export const getProducts = async (category: string): Promise<JsonResponse<Item[]>> =>
  await fetch(`${getLocalURL()}/api/collection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      category,
    }),
  });

export const getBundles = async (): Promise<JsonResponse<Bundle[]>> =>
  await fetch(`${getLocalURL()}/api/collection`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      category: 'preconfigured',
    }),
  });

export const getProductsByCatalog = async (catalogNumbers: string[]): Promise<JsonResponse<Item[]>> =>
  await fetch(`${getLocalURL()}/api/get-products-by-catalog`, {
    method: 'POST',
    body: JSON.stringify({ catalogNumbers }),
    headers: {
      'Content-Type': 'application/json',
    },
  });

export const getBundleBySlug = async (slug: string): Promise<JsonResponse<Bundle>> =>
  await fetch(`${getLocalURL()}/api/bundle/slug`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      slug,
    }),
  });

export const getBundleById = async (id: string): Promise<JsonResponse<Bundle | string>> =>
  await fetch(`${getLocalURL()}/api/bundle/id`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id,
    }),
  });

export const getProductBySlug = async (slug: string): Promise<JsonResponse<Product>> =>
  await fetch(`${getLocalURL()}/api/product/slug`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      slug,
    }),
  });

export interface SurroundingProducts {
  previous: Item | null;
  next: Item | null;
}

/**
 * Returns an object containing the products that surround the given product in a category. Either key may contain null
 * if the given product is the first, last, or only item in its category.
 *
 * This is currently being used to populate the product navigation component on product detail pages.
 */
export const getSurroundingProductsInCategory = async ({ category, id }: Item): Promise<SurroundingProducts> =>
  new Promise(async (resolve) => {
    const callback = category === 'preconfigured' ? getBundles : getProducts;

    const response = await callback(category!);

    const products = (await response.json()).filter((product) =>
      product.category?.toLowerCase() === 'preconfigured' ? !product.is_retail || (product.displayable && !product.rental_bundle) : true,
    );

    const index = products.findIndex((product) => product.id === id);

    resolve({
      previous: products[index - 1] || null,
      next: products[index + 1] || null,
    });
  });

export default productsApi;
